// Neighbourhood sampling and masks (#166 "One rule to rule them all", 12 §8 T9).
//
// The model is a grid of logical kinds (strings). Rules look at the eight neighbours of a cell.
// Off-map cells follow a per-query policy: `mirror` returns the nearest on-map cell (Squidi's
// "99 % of the time perfect border tiles"), `nil` returns the sentinel kind `NIL`, and `as`
// returns a fixed kind (walls at the board edge read as solid rock).

export const NIL = 'NIL';

/** Bit layout shared by every pattern: cardinals in the low nibble, diagonals above. */
export const N = 1, E = 2, S = 4, W = 8, NE = 16, SE = 32, SW = 64, NW = 128;
export const CARDINALS = [N, E, S, W] as const;
export const DIRS: ReadonlyArray<{ bit: number; dx: number; dy: number; name: string }> = [
  { bit: N, dx: 0, dy: -1, name: 'n' }, { bit: E, dx: 1, dy: 0, name: 'e' },
  { bit: S, dx: 0, dy: 1, name: 's' }, { bit: W, dx: -1, dy: 0, name: 'w' },
  { bit: NE, dx: 1, dy: -1, name: 'ne' }, { bit: SE, dx: 1, dy: 1, name: 'se' },
  { bit: SW, dx: -1, dy: 1, name: 'sw' }, { bit: NW, dx: -1, dy: -1, name: 'nw' },
];

export type EdgePolicy = { mode: 'mirror' } | { mode: 'nil' } | { mode: 'as'; kind: string };

export interface KindGrid {
  readonly w: number;
  readonly h: number;
  /** Kind of the cell at (x, y); must be total for on-map coordinates. */
  kindAt(x: number, y: number): string;
}

/** The kind of a square with no board (docs/38 BS6, a free-form field's `Void`). */
export const VOID = 'void';

/**
 * The kind at (x, y) as the cell at (ax, ay) sees it. Off-map squares follow `edge`, and so does
 * a void square (docs/38 BS6: no board is no board, wherever the field ends): `mirror` reads it
 * as the asking cell itself for a side neighbour, and for a corner one as the field square beside
 * it in the asker's column, else its row — the same pieces the rectangle's edge gets.
 */
export function sample(g: KindGrid, x: number, y: number, edge: EdgePolicy, ax = x, ay = y): string {
  if (x >= 0 && y >= 0 && x < g.w && y < g.h) {
    const k = g.kindAt(x, y);
    if (k !== VOID) return k;
    switch (edge.mode) {
      case 'mirror': {
        const inside = (cx: number, cy: number): string | undefined => {
          if (cx < 0 || cy < 0 || cx >= g.w || cy >= g.h) return undefined;
          const c = g.kindAt(cx, cy);
          return c === VOID ? undefined : c;
        };
        return (x !== ax && y !== ay ? inside(ax, y) ?? inside(x, ay) : undefined) ?? g.kindAt(ax, ay);
      }
      case 'nil': return NIL;
      case 'as': return edge.kind;
    }
  }
  switch (edge.mode) {
    case 'mirror': {
      // the nearest on-map square; when that is void, it mirrors in turn (from the same asker)
      const cx = Math.min(g.w - 1, Math.max(0, x)), cy = Math.min(g.h - 1, Math.max(0, y));
      return sample(g, cx, cy, edge, ax, ay);
    }
    case 'nil': return NIL;
    case 'as': return edge.kind;
  }
}

/**
 * 8-neighbour mask of cells for which `same` holds. The diagonal bits are *raw* here; use
 * `reduce47` to apply the blob rule (a diagonal counts only when both adjacent cardinals are set).
 */
export function mask8(g: KindGrid, x: number, y: number, same: (kind: string) => boolean, edge: EdgePolicy): number {
  let m = 0;
  for (const d of DIRS) if (same(sample(g, x + d.dx, y + d.dy, edge, x, y))) m |= d.bit;
  return m;
}

/** The blob reduction: drop diagonal bits whose two adjacent cardinals are not both set. */
export function reduce47(m: number): number {
  let r = m & 0x0f;
  if ((m & N) && (m & E) && (m & NE)) r |= NE;
  if ((m & S) && (m & E) && (m & SE)) r |= SE;
  if ((m & S) && (m & W) && (m & SW)) r |= SW;
  if ((m & N) && (m & W) && (m & NW)) r |= NW;
  return r;
}

/** The 47 reduced masks in a fixed order, so `blobClass(mask)` is a dense 0..46 index. */
export const BLOB47: readonly number[] = (() => {
  const seen = new Set<number>();
  const out: number[] = [];
  for (let m = 0; m < 256; m++) { const r = reduce47(m); if (!seen.has(r)) { seen.add(r); out.push(r); } }
  return out;
})();
const BLOB_INDEX = new Map(BLOB47.map((m, i) => [m, i] as const));

/** Dense class 0..46 of any 8-bit mask (after reduction). */
export function blobClass(m: number): number { return BLOB_INDEX.get(reduce47(m))!; }

/** Cardinal nibble only (fence / rug / wall16 patterns). */
export function mask4(m: number): number { return m & 0x0f; }

/** Inner-corner bits: diagonal NOT set while both adjacent cardinals are set (the "bites"). */
export function innerCorners(m: number): number {
  let r = 0;
  if ((m & N) && (m & E) && !(m & NE)) r |= NE;
  if ((m & S) && (m & E) && !(m & SE)) r |= SE;
  if ((m & S) && (m & W) && !(m & SW)) r |= SW;
  if ((m & N) && (m & W) && !(m & NW)) r |= NW;
  return r;
}

/** Diagonal bits that are set while NEITHER adjacent cardinal is set (lone corner nubs). */
export function loneCorners(m: number): number {
  let r = 0;
  if ((m & NE) && !(m & N) && !(m & E)) r |= NE;
  if ((m & SE) && !(m & S) && !(m & E)) r |= SE;
  if ((m & SW) && !(m & S) && !(m & W)) r |= SW;
  if ((m & NW) && !(m & N) && !(m & W)) r |= NW;
  return r;
}

// ---- Squidi-style explicit rules -----------------------------------------------------------
// A rule matches each named neighbour against: a kind, `!kind`, `*` (anything) or `NIL`.
// Rules run in order; first match wins; this is the escape hatch for decals and odd cases the
// standard patterns do not cover (#166 "specific before broad").

export type Match = Partial<Record<'n' | 'e' | 's' | 'w' | 'ne' | 'se' | 'sw' | 'nw', string>>;
export interface Rule<T> { match: Match; value: T }

export function matches(g: KindGrid, x: number, y: number, m: Match, edge: EdgePolicy, poses: (kind: string, as: string) => boolean): boolean {
  for (const d of DIRS) {
    const want = m[d.name as keyof Match];
    if (want === undefined || want === '*') continue;
    const got = sample(g, x + d.dx, y + d.dy, edge, x, y);
    if (want === NIL) { if (got !== NIL) return false; continue; }
    if (want.startsWith('!')) { if (poses(got, want.slice(1))) return false; continue; }
    if (!poses(got, want)) return false;
  }
  return true;
}

export function firstRule<T>(rules: ReadonlyArray<Rule<T>>, g: KindGrid, x: number, y: number, edge: EdgePolicy, poses: (kind: string, as: string) => boolean): T | undefined {
  for (const r of rules) if (matches(g, x, y, r.match, edge, poses)) return r.value;
  return undefined;
}
